// Stock › Ajustes como GUÍA DE AJUSTE (pedido de Rodrigo 2026-10-21, como la GRI/GDI «por ajuste» de
// Random, con el diseño de las fichas de Compras). Un documento numerado con varios productos, un
// sentido (salida: merma, rotura, vencimiento…; entrada: sobrante), un concepto y un motivo. Cada
// línea es un movimiento de ajuste en el libro (append-only): la guía no se edita; un error se
// corrige con otra guía en sentido contrario. Se ve el stock antes y después, y el valor al PMP.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import { AvisoDoc, CuerpoDoc, DatosDoc, EncabezadoDoc, Tarjeta } from '../compras/FichaDocumento';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { EncabezadoImpreso, Imprimir } from '../comun/Imprimir';
import { decimal, entradaDecimal, fecha, fechaHora, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { BuscadorProducto, type ProductoElegido } from '../ventas/BuscadorProducto';
import { clavesStock, useResumenProductos, useStockDeBodega } from './datos';
import { useBodegas, UltimosAjustes } from './PantallasStock';
import type { SaldoValorizado } from './Valorizacion';

type Sentido = 'SALIDA' | 'ENTRADA';

const CONCEPTOS: Array<{ id: string; nombre: string; sentidos: Sentido[] }> = [
  { id: 'MERMA', nombre: 'Merma', sentidos: ['SALIDA'] },
  { id: 'ROTURA', nombre: 'Rotura', sentidos: ['SALIDA'] },
  { id: 'VENCIMIENTO', nombre: 'Vencimiento', sentidos: ['SALIDA'] },
  { id: 'CONSUMO_INTERNO', nombre: 'Consumo interno', sentidos: ['SALIDA'] },
  { id: 'DEGUSTACION', nombre: 'Degustación / muestra', sentidos: ['SALIDA'] },
  { id: 'ROBO_PERDIDA', nombre: 'Robo o pérdida', sentidos: ['SALIDA'] },
  { id: 'SOBRANTE', nombre: 'Sobrante', sentidos: ['ENTRADA'] },
  { id: 'CORRECCION', nombre: 'Corrección de stock', sentidos: ['SALIDA', 'ENTRADA'] },
  { id: 'OTRO', nombre: 'Otro', sentidos: ['SALIDA', 'ENTRADA'] },
];
const NOMBRE_CONCEPTO = Object.fromEntries(CONCEPTOS.map((c) => [c.id, c.nombre]));

export interface GuiaAjusteResumen {
  id: string;
  numero: number;
  ubicacionId: string;
  sentido: Sentido;
  concepto: string;
  motivo: string;
  fechaNegocio: string;
  creadoEn: string;
  lineas: number;
  unidades: string;
}
interface GuiaAjusteLeida extends GuiaAjusteResumen {
  lineasDetalle: Array<{
    numero: number;
    productoId: string;
    cantidadBase: string;
    saldoAntes: string;
    saldoDespues: string;
  }>;
}

const claveGuias = (e: string) => ['emp', e, 'stock', 'guias-ajuste'] as const;

/** PMP de cada producto (si el usuario puede ver la valorización; si no, el valor queda «—»). */
function usePmp(empresaId: string) {
  const q = useQuery({
    queryKey: ['emp', empresaId, 'stock', 'valorizacion'],
    queryFn: () => api.get<SaldoValorizado[]>('/valorizacion/saldos', { query: { empresaId } }),
    staleTime: 60_000,
    retry: false,
  });
  return useMemo(
    () => new Map((q.data ?? []).map((s) => [s.productoId, s.pmp ? Number(s.pmp) : null])),
    [q.data],
  );
}

const etiquetaSentido = (s: Sentido) => (s === 'SALIDA' ? 'Salida de stock' : 'Entrada de stock');

export function AjustesStock() {
  const { puede, empresa } = useSesion();
  const registra = puede('inventario.ajuste.registrar');
  const [vista, setVista] = useState<'nueva' | 'guias' | 'movimientos'>(
    registra ? 'nueva' : 'guias',
  );
  const [abierta, setAbierta] = useState<string | null>(null);
  if (abierta) return <FichaGuiaAjuste id={abierta} alVolver={() => setAbierta(null)} />;
  const segmentos = [
    ...(registra ? [['nueva', 'Nueva guía de ajuste'] as const] : []),
    ['guias', 'Guías registradas'] as const,
    ['movimientos', 'Todos los movimientos de ajuste'] as const,
  ];
  return (
    <div className="pantalla documento-compra doc-ficha" data-pantalla="ajustes">
      <EncabezadoDoc
        sigla="AJU"
        migas="Stock › Ajustes"
        titulo="Guías de ajuste de stock"
        sub="Merma, rotura, vencimiento, consumo interno o sobrante: cada guía queda numerada y mueve el libro de inventario."
        acciones={
          <div className="segmentos vista-ajuste" role="tablist" aria-label="Vista de ajustes">
            {segmentos.map(([id, texto]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={vista === id}
                className={vista === id ? 'activo' : undefined}
                onClick={() => setVista(id)}
              >
                {texto}
              </button>
            ))}
          </div>
        }
      />
      {!registra && (
        <AvisoDoc tono="info">
          Puedes consultar los ajustes, pero registrarlos requiere el permiso «Registrar ajuste de
          stock».
        </AvisoDoc>
      )}
      {vista === 'nueva' && registra && <EditorGuiaAjuste alVer={setAbierta} />}
      {vista === 'guias' && <ListaGuias alAbrir={setAbierta} />}
      {vista === 'movimientos' && (
        <div className="doc-principal">
          <Tarjeta titulo="Movimientos de ajuste" etiqueta="Últimos ajustes">
            <UltimosAjustes empresaId={empresa!.id} />
          </Tarjeta>
        </div>
      )}
    </div>
  );
}

// ================================================================ editor

interface LineaEditor {
  clave: string;
  p: ProductoElegido;
  cantidad: string;
}

function EditorGuiaAjuste({ alVer }: { alVer: (id: string) => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const intentos = useIntentos();
  const bodegas = useBodegas(empresaId);
  const [elegida, setElegida] = useState('');
  const ubicacionId = elegida || bodegas.lista[0]?.id || '';
  const stock = useStockDeBodega(empresaId, ubicacionId);
  const pmp = usePmp(empresaId);
  const [sentido, setSentido] = useState<Sentido>('SALIDA');
  const [concepto, setConcepto] = useState('MERMA');
  const [fechaNegocio, setFechaNegocio] = useState(() =>
    new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Santiago' }),
  );
  const [motivo, setMotivo] = useState('');
  const [lineas, setLineas] = useState<LineaEditor[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [hecha, setHecha] = useState<{ id: string; numero: number } | null>(null);
  const saldo = useMemo(
    () => new Map((stock.data ?? []).map((f) => [f.productoId, Number(f.fisico)])),
    [stock.data],
  );
  const conceptos = CONCEPTOS.filter((c) => c.sentidos.includes(sentido));
  const signo = sentido === 'SALIDA' ? -1 : 1;

  // Unidad base de cada línea: lo digitado × factor de la presentación elegida (caja = 12 UN…).
  const calc = lineas.map((l) => {
    const t = entradaDecimal(l.cantidad, 4);
    const factor = Number(l.p.presentacion.factor_a_base);
    const base = t ? Number(t) * factor : 0;
    const antes = saldo.get(l.p.producto.id) ?? 0;
    const costo = pmp.get(l.p.producto.id) ?? null;
    return {
      l,
      valida: !!t && Number(t) > 0,
      base,
      antes,
      despues: antes + signo * base,
      costo,
      valor: costo !== null ? Math.round(costo * base) : null,
    };
  });
  const totalUnidades = calc.reduce((s, x) => s + x.base, 0);
  const totalValor = calc.reduce((s, x) => s + (x.valor ?? 0), 0);
  const sinCosto = calc.some((x) => x.costo === null);
  const negativos = calc.filter((x) => x.despues < 0);
  const valido =
    !!ubicacionId &&
    calc.length > 0 &&
    calc.every((x) => x.valida) &&
    motivo.trim().length >= 5 &&
    conceptos.some((c) => c.id === concepto);

  const registrar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        empresaId,
        ubicacionId,
        sentido,
        concepto,
        fechaNegocio,
        motivo: motivo.trim(),
        lineas: calc.map((x) => ({
          productoId: x.l.p.producto.id,
          cantidad: String(Number(x.base.toFixed(4))),
        })),
      };
      return api.post<{ ajusteId: string; numero: number }>('/inventario/guias-ajuste', cuerpo, {
        idempotencia: intentos.clave('guia-ajuste', cuerpo),
      });
    },
    onSuccess: async (r) => {
      intentos.cerrar();
      setHecha({ id: r.ajusteId, numero: r.numero });
      setLineas([]);
      setMotivo('');
      await qc.invalidateQueries({ queryKey: clavesStock.stock(empresaId) });
      await qc.invalidateQueries({ queryKey: claveGuias(empresaId) });
    },
    onError: (e) => intentos.cerrar(e),
  });

  // F3 agrega un producto · F9 registra (como en Ventas y Compras).
  useEffect(() => {
    const t = (e: KeyboardEvent) => {
      if (e.key === 'F3') {
        e.preventDefault();
        setBuscando(true);
      } else if (e.key === 'F9' && valido && !registrar.isPending) {
        e.preventDefault();
        registrar.mutate();
      }
    };
    window.addEventListener('keydown', t);
    return () => window.removeEventListener('keydown', t);
  }, [valido, registrar]);

  const cambiarSentido = (s: Sentido) => {
    setSentido(s);
    if (!CONCEPTOS.find((c) => c.id === concepto)?.sentidos.includes(s))
      setConcepto(s === 'SALIDA' ? 'MERMA' : 'SOBRANTE');
  };

  return (
    <>
      {hecha && (
        <AvisoDoc tono="ok">
          <b>Guía de ajuste N° {hecha.numero} registrada.</b> El stock ya quedó actualizado.{' '}
          <button type="button" className="enlace" onClick={() => alVer(hecha.id)}>
            Ver la guía
          </button>
        </AvisoDoc>
      )}
      {registrar.isError && (
        <div className="doc-aviso tono-malo">
          <PanelError error={registrar.error} />
        </div>
      )}
      <div className="doc-principal">
        <Tarjeta titulo="Datos de la guía" etiqueta="Datos de la guía">
          <form
            className="campos-ajuste"
            aria-label="Registrar ajuste de stock"
            onSubmit={(e) => {
              e.preventDefault();
              if (valido && !registrar.isPending) registrar.mutate();
            }}
          >
            <div className="campo sentido-ajuste" role="radiogroup" aria-label="Sentido del ajuste">
              <span className="etiqueta">Tipo de ajuste</span>
              <div>
                <button
                  type="button"
                  role="radio"
                  aria-checked={sentido === 'SALIDA'}
                  className={`salida ${sentido === 'SALIDA' ? 'activo' : ''}`}
                  onClick={() => cambiarSentido('SALIDA')}
                >
                  ▼ Salida <span>merma, rotura, vencimiento…</span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={sentido === 'ENTRADA'}
                  className={`entrada ${sentido === 'ENTRADA' ? 'activo' : ''}`}
                  onClick={() => cambiarSentido('ENTRADA')}
                >
                  ▲ Entrada <span>sobrante</span>
                </button>
              </div>
            </div>
            <label className="campo">
              <span className="etiqueta">Bodega</span>
              <select
                aria-label="Bodega"
                value={ubicacionId}
                onChange={(e) => setElegida(e.target.value)}
              >
                {bodegas.lista.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.codigo} · {b.nombre}
                  </option>
                ))}
              </select>
            </label>
            <label className="campo">
              <span className="etiqueta">Concepto</span>
              <select
                aria-label="Concepto"
                value={concepto}
                onChange={(e) => setConcepto(e.target.value)}
              >
                {conceptos.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </label>
            <label className="campo">
              <span className="etiqueta">Fecha</span>
              <input
                type="date"
                aria-label="Fecha del ajuste"
                value={fechaNegocio}
                onChange={(e) => setFechaNegocio(e.target.value)}
              />
            </label>
            <label className="campo motivo-ajuste">
              <span className="etiqueta">Motivo (obligatorio, mínimo 5 letras)</span>
              <input
                aria-label="Motivo del ajuste"
                placeholder="Ej.: 3 botellas rotas al ordenar el pasillo 4"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
            </label>
          </form>
        </Tarjeta>
        <Tarjeta
          titulo="Productos"
          etiqueta="Productos de la guía"
          extra={
            <button type="button" className="boton-mini" onClick={() => setBuscando(true)}>
              + Agregar producto <kbd>F3</kbd>
            </button>
          }
        >
          {lineas.length === 0 ? (
            <p className="vacio-tarjeta">
              Agrega los productos con <b>F3</b> o con «+ Agregar producto». Puedes digitar en cajas
              o unidades: el sistema lo pasa a unidades.
            </p>
          ) : (
            <table className="grilla compacta" aria-label="Líneas del ajuste">
              <thead>
                <tr>
                  <th style={{ width: '2.5rem' }}>N°</th>
                  <th style={{ width: '7rem' }}>Código</th>
                  <th>Producto</th>
                  <th style={{ width: '6rem' }}>Present.</th>
                  <th className="der">Stock actual</th>
                  <th className="der" style={{ width: '9rem' }}>
                    {sentido === 'SALIDA' ? 'Sale' : 'Entra'}
                  </th>
                  <th className="der">Stock después</th>
                  <th className="der">Costo (PMP)</th>
                  <th className="der">Valor</th>
                  <th style={{ width: '2rem' }} />
                </tr>
              </thead>
              <tbody>
                {calc.map((x, i) => (
                  <tr key={x.l.clave}>
                    <td>{i + 1}</td>
                    <td className="mono">{x.l.p.producto.sku}</td>
                    <td>{x.l.p.producto.nombre}</td>
                    <td>{x.l.p.presentacion.codigo}</td>
                    <td className="der">{decimal(String(x.antes))}</td>
                    <td className="der">
                      <input
                        className={`celda-num ${x.l.cantidad && !x.valida ? 'con-error' : ''}`}
                        inputMode="decimal"
                        aria-label={`Cantidad ${x.l.p.producto.sku}`}
                        value={x.l.cantidad}
                        onChange={(e) =>
                          setLineas((ls) =>
                            ls.map((y) =>
                              y.clave === x.l.clave ? { ...y, cantidad: e.target.value } : y,
                            ),
                          )
                        }
                      />
                      {Number(x.l.p.presentacion.factor_a_base) !== 1 && x.valida && (
                        <span className="apagado ayuda-base">
                          = {decimal(String(x.base))} {x.l.p.base.codigo}
                        </span>
                      )}
                    </td>
                    <td className={`der ${x.despues < 0 ? 'tono-malo' : ''}`}>
                      <b>{decimal(String(x.despues))}</b>
                    </td>
                    <td className="der">
                      {x.costo !== null ? pesos(String(Math.round(x.costo))) : '—'}
                    </td>
                    <td className="der">
                      {x.valor !== null ? pesos(String(signo * x.valor)) : '—'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="boton-mini"
                        aria-label={`Quitar ${x.l.p.producto.sku}`}
                        onClick={() => setLineas((ls) => ls.filter((y) => y.clave !== x.l.clave))}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {negativos.length > 0 && (
            <p className="tono-malo">
              {negativos.length === 1
                ? 'Un producto queda'
                : `${negativos.length} productos quedan`}{' '}
              con stock negativo: revisa la cantidad antes de registrar.
            </p>
          )}
        </Tarjeta>
        <div className="pie-ajuste">
          <DatosDoc
            datos={[
              { e: 'Productos', v: String(lineas.length) },
              { e: 'Unidades', v: decimal(String(totalUnidades)) },
              {
                e: sentido === 'SALIDA' ? 'Valor que sale' : 'Valor que entra',
                v: totalValor ? pesos(String(signo * totalValor)) : '—',
                fuerte: true,
              },
            ]}
          />
          {sinCosto && lineas.length > 0 && (
            <span className="apagado">
              Algún producto no tiene costo aprobado: su valor queda «—».
            </span>
          )}
          <button
            type="button"
            className="primario grande"
            disabled={!valido || registrar.isPending}
            title="F9"
            onClick={() => registrar.mutate()}
          >
            {registrar.isPending ? 'Registrando…' : 'Registrar ajuste'} <kbd>F9</kbd>
          </button>
        </div>
      </div>
      {buscando && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={null}
          ubicacionId={ubicacionId}
          ubicaciones={bodegas.lista}
          alElegir={(p) => {
            setBuscando(false);
            setLineas((ls) =>
              ls.some((y) => y.p.producto.id === p.producto.id)
                ? ls
                : [...ls, { clave: `${p.producto.id}-${Date.now()}`, p, cantidad: '' }],
            );
            setHecha(null);
          }}
          alCerrar={() => setBuscando(false)}
        />
      )}
    </>
  );
}

// ================================================================ lista y ficha

function useGuias(empresaId: string) {
  return useQuery({
    queryKey: claveGuias(empresaId),
    queryFn: () =>
      api.get<GuiaAjusteResumen[]>('/inventario/guias-ajuste', { query: { empresaId } }),
  });
}

function ListaGuias({ alAbrir }: { alAbrir: (id: string) => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const q = useGuias(empresaId);
  const bodegas = useBodegas(empresaId);
  const nombreBodega = (id: string) => bodegas.lista.find((b) => b.id === id)?.codigo ?? '…';
  const columnas: Columna<GuiaAjusteResumen>[] = [
    { id: 'n', titulo: 'N°', celda: (g) => <b>AJU {g.numero}</b>, ancho: '6rem' },
    { id: 'f', titulo: 'Fecha', celda: (g) => fecha(g.fechaNegocio), ancho: '7rem' },
    { id: 'b', titulo: 'Bodega', celda: (g) => nombreBodega(g.ubicacionId), ancho: '5rem' },
    {
      id: 's',
      titulo: 'Tipo',
      celda: (g) => (
        <span className={`insignia ${g.sentido === 'SALIDA' ? 'tono-malo' : 'tono-ok'}`}>
          {g.sentido === 'SALIDA' ? '▼ Salida' : '▲ Entrada'}
        </span>
      ),
      ancho: '7rem',
    },
    { id: 'c', titulo: 'Concepto', celda: (g) => NOMBRE_CONCEPTO[g.concepto] ?? g.concepto },
    { id: 'l', titulo: 'Productos', celda: (g) => g.lineas, alinear: 'der', ancho: '6rem' },
    {
      id: 'u',
      titulo: 'Unidades',
      celda: (g) => decimal(g.unidades),
      alinear: 'der',
      ancho: '7rem',
    },
    { id: 'm', titulo: 'Motivo', celda: (g) => g.motivo },
  ];
  return (
    <div className="doc-principal">
      {q.isError && <PanelError error={q.error} />}
      <Tarjeta
        titulo="Guías registradas"
        etiqueta="Guías de ajuste"
        extra={q.data ? `${q.data.length} guías · doble clic o Enter para abrir` : undefined}
      >
        <Grilla
          etiqueta="Lista de guías de ajuste"
          columnas={columnas}
          filas={q.data ?? []}
          clave={(g) => g.id}
          alAbrir={(g) => alAbrir(g.id)}
          cargando={q.isLoading}
          vacio="Todavía no hay guías de ajuste"
        />
      </Tarjeta>
    </div>
  );
}

function FichaGuiaAjuste({ id, alVolver }: { id: string; alVolver: () => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const q = useQuery({
    queryKey: [...claveGuias(empresaId), id],
    queryFn: () =>
      api.get<GuiaAjusteLeida>(`/inventario/guias-ajuste/${id}`, { query: { empresaId } }),
  });
  const bodegas = useBodegas(empresaId);
  const pmp = usePmp(empresaId);
  const productos = useResumenProductos(
    empresaId,
    (q.data?.lineasDetalle ?? []).map((l) => l.productoId),
  );
  const [imprimir, setImprimir] = useState(false);
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return <p className="apagado pantalla">Cargando guía…</p>;
  const g = q.data;
  const bodega = bodegas.lista.find((b) => b.id === g.ubicacionId);
  const salida = g.sentido === 'SALIDA';
  const valor = g.lineasDetalle.reduce((s, l) => {
    const c = pmp.get(l.productoId);
    return c != null ? s + Math.round(c * Number(l.cantidadBase)) : s;
  }, 0);
  return (
    <div className="pantalla documento-compra doc-ficha" data-pantalla="guia-ajuste">
      <EncabezadoDoc
        sigla="AJU"
        migas="Stock › Ajustes"
        titulo={`Guía de ajuste N° ${g.numero}`}
        insignias={
          <span className={`insignia ${salida ? 'tono-malo' : 'tono-ok'}`}>
            {salida ? '▼ Salida de stock' : '▲ Entrada de stock'}
          </span>
        }
        sub={
          <>
            {NOMBRE_CONCEPTO[g.concepto] ?? g.concepto} · {bodega?.nombre ?? 'bodega'} ·{' '}
            {fecha(g.fechaNegocio)}
          </>
        }
        acciones={
          <>
            <button type="button" onClick={alVolver}>
              ← Volver a ajustes
            </button>
            <button type="button" onClick={() => setImprimir(true)}>
              Imprimir guía
            </button>
          </>
        }
      />
      <AvisoDoc tono={salida ? 'aviso' : 'info'}>
        <b>Motivo:</b> {g.motivo}
      </AvisoDoc>
      <CuerpoDoc
        principal={
          <Tarjeta titulo="Productos ajustados" extra={`${g.lineas} productos`}>
            <table className="grilla compacta" aria-label="Productos ajustados">
              <thead>
                <tr>
                  <th style={{ width: '2.5rem' }}>N°</th>
                  <th style={{ width: '7rem' }}>Código</th>
                  <th>Producto</th>
                  <th className="der">Stock antes</th>
                  <th className="der">{salida ? 'Salió' : 'Entró'}</th>
                  <th className="der">Stock después</th>
                </tr>
              </thead>
              <tbody>
                {g.lineasDetalle.map((l) => {
                  const p = productos.mapa.get(l.productoId);
                  return (
                    <tr key={l.numero}>
                      <td>{l.numero}</td>
                      <td className="mono">{p?.sku ?? '…'}</td>
                      <td>{p?.nombre ?? '…'}</td>
                      <td className="der">{decimal(l.saldoAntes)}</td>
                      <td className={`der ${salida ? 'tono-malo' : 'tono-ok'}`}>
                        <b>
                          {salida ? '−' : '+'}
                          {decimal(l.cantidadBase)}
                        </b>
                      </td>
                      <td className="der">{decimal(l.saldoDespues)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Tarjeta>
        }
        lateral={
          <>
            <Tarjeta titulo="Datos" etiqueta="Datos de la guía">
              <DatosDoc
                datos={[
                  { e: 'Concepto', v: NOMBRE_CONCEPTO[g.concepto] ?? g.concepto },
                  { e: 'Bodega', v: bodega ? `${bodega.codigo} · ${bodega.nombre}` : '…' },
                  { e: 'Fecha', v: fecha(g.fechaNegocio) },
                  { e: 'Registrada', v: fechaHora(g.creadoEn) },
                ]}
              />
            </Tarjeta>
            <Tarjeta titulo="Totales" etiqueta="Totales de la guía">
              <DatosDoc
                datos={[
                  { e: 'Productos', v: String(g.lineas) },
                  { e: 'Unidades', v: decimal(g.unidades) },
                  {
                    e: 'Valor al PMP actual',
                    v: valor ? pesos(String(salida ? -valor : valor)) : '—',
                    fuerte: true,
                  },
                ]}
              />
            </Tarjeta>
          </>
        }
      />
      {imprimir && (
        <Imprimir alTerminar={() => setImprimir(false)}>
          <article className="impreso">
            <EncabezadoImpreso
              empresa={empresa!.razon_social}
              titulo={`Guía de ajuste · ${etiquetaSentido(g.sentido)}`}
              numero={`AJU ${g.numero}`}
              detalle={
                <dl className="impreso-datos">
                  <dt>Bodega</dt>
                  <dd>{bodega?.nombre}</dd>
                  <dt>Fecha</dt>
                  <dd>{fecha(g.fechaNegocio)}</dd>
                  <dt>Concepto</dt>
                  <dd>{NOMBRE_CONCEPTO[g.concepto] ?? g.concepto}</dd>
                  <dt>Motivo</dt>
                  <dd>{g.motivo}</dd>
                </dl>
              }
            />
            <table className="impreso-tabla">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descripción</th>
                  <th className="der">Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {g.lineasDetalle.map((l) => (
                  <tr key={l.numero}>
                    <td>{productos.mapa.get(l.productoId)?.sku}</td>
                    <td>{productos.mapa.get(l.productoId)?.nombre}</td>
                    <td className="der">{decimal(l.cantidadBase)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ marginTop: '3rem' }}>Autorizado por: ____________ Bodega: ____________</p>
          </article>
        </Imprimir>
      )}
    </div>
  );
}
